// The sounds themselves, synthesized through the Web Audio API: no files, no
// dependency, nothing to load. Each sound is a recipe of a few notes, a tone
// (an oscillator) or a burst of filtered noise, each with its start, length
// and loudness and an envelope that rises in 5 ms and dies away. A move is a
// crisp click (12 ms of high-passed noise, dry like a key; the user chose it
// on 2026-10-17 from seven candidates over the first wooden tap), a capture
// a longer click over a low tone, a check a click with a bright ping after
// it, castling two clicks, a jump a dull thud alone; the puzzle's chimes are two rising tones for
// solved and one falling tone for a miss, both a beat after the move's own
// sound. The browser lets audio start only after a user gesture, so the
// context is made or resumed on the page's first click or key
// (`unlock`), and `play` resumes it again if it was suspended meanwhile.
import type { Sound } from './sound-of.ts';

export interface Note {
  kind: 'sine' | 'triangle' | 'noise';
  /** The tone's frequency, or the noise filter's frequency, in Hz. */
  freq: number;
  /** The noise's filter; band-pass when not said. */
  filter?: 'bandpass' | 'highpass' | 'lowpass';
  /** Where the tone slides to by its end, when it does. */
  to?: number;
  /** Seconds after the sound starts. */
  start: number;
  /** Seconds, envelope included. */
  duration: number;
  /** Peak gain, 0–1, before the master volume. */
  gain: number;
}

const tap: Note[] = [{ kind: 'noise', filter: 'highpass', freq: 3500, start: 0, duration: 0.012, gain: 0.6 }];

export const RECIPES: Record<Sound, Note[]> = {
  move: tap,
  capture: [
    { kind: 'noise', filter: 'highpass', freq: 2000, start: 0, duration: 0.025, gain: 0.8 },
    { kind: 'sine', freq: 150, start: 0, duration: 0.05, gain: 0.3 },
  ],
  check: [...tap, { kind: 'triangle', freq: 880, start: 0.03, duration: 0.09, gain: 0.18 }],
  castle: [...tap, ...tap.map((n) => ({ ...n, start: n.start + 0.09 }))],
  jump: [{ kind: 'sine', freq: 90, to: 60, start: 0, duration: 0.13, gain: 0.45 }],
  solved: [
    { kind: 'sine', freq: 660, start: 0.12, duration: 0.1, gain: 0.25 },
    { kind: 'sine', freq: 990, start: 0.21, duration: 0.16, gain: 0.25 },
  ],
  failed: [{ kind: 'triangle', freq: 220, to: 170, start: 0.12, duration: 0.18, gain: 0.3 }],
};

/** The master volume: low, fixed. */
export const VOLUME = 0.3;

export class Synth {
  private ctx?: AudioContext;
  private noise?: AudioBuffer;
  private readonly create: () => AudioContext;
  private readonly warn: (message: string) => void;

  constructor(create: () => AudioContext = () => new AudioContext(), warn: (message: string) => void = () => {}) {
    this.create = create;
    this.warn = warn;
  }

  /** Makes the context, or resumes it, inside a user gesture, where the browser allows it. */
  unlock(): void {
    const ctx = this.context();
    if (ctx?.state === 'suspended') void ctx.resume().catch(() => {});
  }

  play(sound: Sound): void {
    const ctx = this.context();
    if (!ctx) return;
    if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
    const at = ctx.currentTime + 0.005;
    for (const note of RECIPES[sound]) this.note(ctx, note, at);
  }

  private context(): AudioContext | undefined {
    if (this.ctx) return this.ctx;
    try {
      this.ctx = this.create();
    } catch (e) {
      this.warn(`no audio: ${e instanceof Error ? e.message : String(e)}`);
    }
    return this.ctx;
  }

  private note(ctx: AudioContext, note: Note, at: number): void {
    const t = at + note.start;
    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, t);
    env.gain.linearRampToValueAtTime(note.gain * VOLUME, t + 0.005);
    env.gain.exponentialRampToValueAtTime(0.0001, t + note.duration);
    env.connect(ctx.destination);
    if (note.kind === 'noise') {
      const src = ctx.createBufferSource();
      src.buffer = this.noiseBuffer(ctx);
      const filter = ctx.createBiquadFilter();
      filter.type = note.filter ?? 'bandpass';
      filter.frequency.value = note.freq;
      filter.Q.value = 1;
      src.connect(filter).connect(env);
      src.start(t);
      src.stop(t + note.duration);
    } else {
      const osc = ctx.createOscillator();
      osc.type = note.kind;
      osc.frequency.setValueAtTime(note.freq, t);
      if (note.to !== undefined) osc.frequency.exponentialRampToValueAtTime(note.to, t + note.duration);
      osc.connect(env);
      osc.start(t);
      osc.stop(t + note.duration);
    }
  }

  /** A fifth of a second of white noise, made once. */
  private noiseBuffer(ctx: AudioContext): AudioBuffer {
    if (this.noise) return this.noise;
    const length = Math.floor(ctx.sampleRate * 0.2);
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
    this.noise = buffer;
    return buffer;
  }
}
